import { useEffect, useState } from "react";
import { Warehouse, Zap } from "lucide-react";
import { toast } from "sonner";
import { appApi, formatApiErrorDetail, fmtDate } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";

export default function AdminWarehouses() {
  const [tab, setTab] = useState("pending");
  const [data, setData] = useState(null);
  const [counts, setCounts] = useState(null);
  const [syncIds, setSyncIds] = useState({});
  const [busy, setBusy] = useState("");

  const loadCounts = () => appApi.get("admin/warehouses").then((d) => setCounts(d.counts || null)).catch(() => {});

  const load = () => {
    setData(null);
    const params = tab === "pending" ? {} : { status_filter: tab };
    appApi.get("admin/warehouses", params).then(setData).catch(() => setData({ warehouses: [] }));
  };
  useEffect(() => { load(); loadCounts(); /* eslint-disable-next-line */ }, [tab]);

  const sync = async (w) => {
    const sid = (syncIds[w.id] || w.shipmozo_warehouse_id || "").trim();
    if (!sid) {
      toast.error("Enter the Shipmozo warehouse ID first.");
      return;
    }
    setBusy(w.id);
    try {
      await appApi.post(`admin/warehouses/${w.id}/sync`, { shipmozo_warehouse_id: sid });
      toast.success(`${w.address_title} synced — warehouse activated for bookings.`);
      load();
      loadCounts();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const warehouses = data?.warehouses || [];

  return (
    <div data-testid="admin-warehouses-page" className="space-y-6">
      <div>
        <p className="text-xs font-mono font-bold uppercase tracking-[0.2em] text-blaze">Admin</p>
        <h1 className="font-display font-black tracking-tight text-3xl sm:text-4xl mt-1">Warehouse Activation</h1>
        <p className="text-sm text-zinc-500 mt-2">Sync merchant pickup warehouses to the courier network so booking unlocks.</p>
      </div>

      {counts && (
        <div className="grid grid-cols-3 border-2 border-obsidian bg-white max-w-2xl" data-testid="warehouse-counts">
          {[
            { l: "Total", v: counts.total, t: "wh-count-total" },
            { l: "Pending", v: counts.pending, t: "wh-count-pending" },
            { l: "Synced", v: counts.synced, t: "wh-count-synced" },
          ].map((c, i) => (
            <div key={c.t} data-testid={c.t} className={`p-5 ${i !== 0 ? "border-l-2 border-obsidian" : ""}`}>
              <p className="text-[11px] font-mono font-bold uppercase tracking-widest text-zinc-500">{c.l}</p>
              <p className="font-display font-black text-3xl tracking-tight mt-1">{c.v}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-0 border-2 border-obsidian w-fit bg-white" data-testid="warehouse-tabs">
        {["pending", "synced"].map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`warehouse-tab-${t}`}
            className={`px-5 py-2.5 text-xs font-mono font-bold uppercase tracking-widest transition-colors duration-150 ${tab === t ? "bg-obsidian text-sky-300" : "hover:bg-acid/40"}`}>
            {t}
          </button>
        ))}
      </div>

      <div className="space-y-4" data-testid="warehouses-list">
        {warehouses.map((w) => (
          <div key={w.id} className="border-2 border-obsidian bg-white p-5" data-testid={`warehouse-card-${w.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 bg-obsidian text-sky-300 flex items-center justify-center"><Warehouse className="w-5 h-5" /></span>
                <div>
                  <p className="font-bold text-sm">{w.address_title}{w.is_default ? " · default" : ""}</p>
                  <p className="text-xs font-mono text-zinc-500">
                    {w.merchant?.name} ({w.merchant?.email}) · {w.merchant?.business || ""}
                  </p>
                  <p className="text-xs text-zinc-500 mt-0.5">
                    {w.address_line_1}{w.address_line_2 ? `, ${w.address_line_2}` : ""}, {w.city}, {w.state} — {w.pincode} · {w.contact_name} {w.phone}
                  </p>
                </div>
              </div>
              <div className="flex gap-1.5">
                <StatusBadge status={w.status} />
                {w.sync_status && <StatusBadge status={w.sync_status} />}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-4 text-xs font-mono text-zinc-500">
              <span>Added {fmtDate(w.created_at)}</span>
              {w.shipmozo_warehouse_id && <span>· Shipmozo ID: <span className="font-bold text-obsidian">{w.shipmozo_warehouse_id}</span></span>}
              {w.shiprocket_b2c_pickup_nick && <span>· B2C nick: {w.shiprocket_b2c_pickup_nick}</span>}
            </div>
            {String(w.sync_status).toLowerCase() !== "synced" && (
              <div className="flex flex-wrap gap-3 mt-4 items-center">
                <input
                  value={syncIds[w.id] ?? (w.shipmozo_warehouse_id || "")}
                  onChange={(e) => setSyncIds((s) => ({ ...s, [w.id]: e.target.value }))}
                  placeholder="Shipmozo warehouse ID"
                  data-testid={`warehouse-sync-id-${w.id}`}
                  className="border-2 border-obsidian bg-paper px-4 py-2.5 text-sm font-mono outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian w-64"
                />
                <button onClick={() => sync(w)} disabled={busy === w.id} data-testid={`warehouse-sync-${w.id}`}
                  className="inline-flex items-center gap-1.5 bg-acid text-white text-xs font-bold uppercase tracking-widest px-5 py-3 border-2 border-obsidian shadow-[3px_3px_0px_0px_#0B1E3A] hover:shadow-none hover:translate-x-[3px] hover:translate-y-[3px] transition-all duration-150 disabled:opacity-60">
                  <Zap className="w-3.5 h-3.5" /> {busy === w.id ? "Syncing…" : "Sync & Activate"}
                </button>
              </div>
            )}
          </div>
        ))}
        {data && warehouses.length === 0 && (
          <div className="border-2 border-obsidian bg-white p-10 text-sm text-zinc-500" data-testid="warehouses-empty">
            No {tab} warehouses right now.
          </div>
        )}
        {!data && <p className="font-mono text-xs uppercase tracking-[0.3em] text-zinc-400">Loading warehouses…</p>}
      </div>
    </div>
  );
}
